import api from '@/client/axiosInstance';
import { AdminRoute } from '@/constants';
import {
  Chat,
  EngagementAttachment,
  EngagementOutcome,
  FreeListingRequestRow,
  FreeListingStats,
} from '@/types';
import { isAxiosError } from 'axios';

// class-validator 400s carry message as an array of strings. Matches the
// mapper in api/territoryCheck.ts.
const toError = (error: unknown): Error => {
  if (isAxiosError(error)) {
    const serverMessage = error.response?.data?.message;
    return new Error(
      (Array.isArray(serverMessage)
        ? serverMessage.join(', ')
        : serverMessage) || 'Something went wrong. Please try again.',
    );
  }
  return new Error('Unknown error occurred');
};

export const getFreeListingRequests = async (filters: {
  search?: string;
  requestType?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  page?: number;
  limit?: number;
}): Promise<{
  total: number;
  page: number;
  limit: number;
  requests: FreeListingRequestRow[];
}> => {
  try {
    const response = await api.get(AdminRoute.freeListingRequests, {
      params: filters,
    });
    return response.data;
  } catch (error) {
    throw toError(error);
  }
};

export const getFreeListingStats = async (): Promise<FreeListingStats> => {
  try {
    const response = await api.get(AdminRoute.freeListingStats);
    return response.data;
  } catch (error) {
    throw toError(error);
  }
};

export const respondToFreeListingRequest = async (
  cardId: string,
  payload: { outcome: EngagementOutcome; responseBody?: string },
): Promise<unknown> => {
  try {
    const response = await api.post(
      AdminRoute.freeListingRespond(cardId),
      payload,
    );
    return response.data;
  } catch (error) {
    throw toError(error);
  }
};

export const replyToFreeListingThread = async (
  chatId: string,
  content: string,
  // Optional and can be sent alone (no content) — the backend accepts a
  // reply with neither text nor an attachment only as a rejection, mirroring
  // the shared ChatInput/chat page's own "text or files" rule.
  attachments?: EngagementAttachment[],
  // TCX-903 follow-up: WhatsApp-style reply quoting — the `_id` of the
  // message being replied to, same field name as chat's own SendMessageDto
  // (see AdminReplyDto.replyTo). The backend re-verifies it names a plain
  // message in this same chat before persisting it.
  replyTo?: string,
  // TCX-725: stable per send ATTEMPT. The composer restores the draft when a
  // reply fails, so a reply whose response was lost after the write IS retried
  // by a human — this is what stops that retry appending a second copy.
  clientMessageId?: string,
): Promise<{ messageId: string }> => {
  try {
    const response = await api.post(AdminRoute.freeListingReply(chatId), {
      content,
      attachments,
      replyTo,
      clientMessageId,
    });
    return response.data;
  } catch (error) {
    throw toError(error);
  }
};

// The consultant-facing GET chats/:chatId cannot serve this drawer: it gates
// on the caller being a participant, and an admin never is one for a Free
// Listing thread. This hits the dedicated admin read instead, whose own
// precondition is the Free Listing rule rather than participancy.
export const getFreeListingThread = async (chatId: string): Promise<Chat> => {
  try {
    const response = await api.get(AdminRoute.freeListingThread(chatId));
    return response.data;
  } catch (error) {
    throw toError(error);
  }
};
